import { useEffect, useRef, type CSSProperties } from 'react';
import { MagneticCta } from '../components/MagneticCta';
import { useMotionReady } from '../hooks/useMotionReady';
import { PetCanvas } from '../components/PetCanvas';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { siteConfig } from '../../../shared/config/site';
import { scrollToSection } from '../../../shared/lib/scroll';
import { loadGsap } from '../../../shared/lib/gsap';

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const brandRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const motionReady = useMotionReady();
  // The cutout carries the hero on its own; the 880kB three.js scene is an
  // enhancement, so it waits for the visitor to move rather than for load.
  const showCanvas = motionReady && !prefersReducedMotion;

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || prefersReducedMotion) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const rect = stage.getBoundingClientRect();
      const x = (event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5;
      const y = (event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5;
      stage.style.setProperty('--hero-x', x.toFixed(3));
      stage.style.setProperty('--hero-y', y.toFixed(3));
    };

    const reset = () => {
      stage.style.setProperty('--hero-x', '0');
      stage.style.setProperty('--hero-y', '0');
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', reset);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', reset);
    };
  }, [prefersReducedMotion]);

  useEffect(() => {
    const section = sectionRef.current;
    const brand = brandRef.current;

    if (!section || !brand || prefersReducedMotion) {
      return;
    }

    let killed = false;
    let tween: gsap.core.Tween | null = null;
    let started = false;

    const start = () => {
      if (started || killed) {
        return;
      }
      started = true;

      void loadGsap().then(({ gsap }) => {
        if (killed) {
          return;
        }

        tween = gsap.to(brand, {
          y: -60,
          scale: 0.92,
          opacity: 0.35,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      });
    };

    const onScroll = () => start();
    window.addEventListener('scroll', onScroll, { passive: true, once: true });

    return () => {
      killed = true;
      window.removeEventListener('scroll', onScroll);
      tween?.scrollTrigger?.kill();
      tween?.kill();
    };
  }, [prefersReducedMotion]);

  const handleVisitClick = () => {
    void scrollToSection('visit');
  };

  const handleCareClick = () => {
    void scrollToSection('care');
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative isolate min-h-[100dvh] overflow-hidden bg-stage text-mist"
      aria-labelledby="hero-heading"
    >
      <div
        ref={stageRef}
        className="absolute inset-0 z-0"
        aria-hidden="true"
        style={{ '--hero-x': '0', '--hero-y': '0' } as CSSProperties}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 55% at 78% 58%, rgba(196,165,116,0.22), transparent 58%), radial-gradient(ellipse 50% 45% at 18% 18%, rgba(47,111,82,0.4), transparent 55%), radial-gradient(ellipse 80% 50% at 50% 100%, rgba(8,18,14,0.85), transparent 50%), #0B1F18',
          }}
        />
        <picture>
          <source media="(max-width: 640px)" srcSet="/images/dog-01-xs.webp" type="image/webp" />
          <img
            src="/images/dog-01-sm.webp"
            alt=""
            width={793}
            height={1200}
            className={`pointer-events-none absolute bottom-[22%] right-[-8%] z-[1] h-[48%] w-auto max-w-none object-contain transition-opacity duration-300 ease-in-out will-change-transform sm:bottom-0 sm:right-[0%] sm:h-[84%] lg:right-[4%] lg:h-[92%] ${
              showCanvas ? 'opacity-35 sm:opacity-20' : 'opacity-100'
            }`}
            style={{
              transform: prefersReducedMotion
                ? undefined
                : 'translate3d(calc(var(--hero-x) * 36px), calc(var(--hero-y) * -22px), 0) scale(1.02)',
            }}
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <img
          src="/images/cat-03-sm.webp"
          alt=""
          width={509}
          height={600}
          className="pointer-events-none absolute bottom-[8%] left-[-8%] z-[1] h-[22%] w-auto object-contain opacity-45 will-change-transform sm:left-[1%] sm:h-[36%] sm:opacity-55 lg:left-[6%] lg:h-[44%]"
          style={{
            transform: prefersReducedMotion
              ? undefined
              : 'translate3d(calc(var(--hero-x) * -24px), calc(var(--hero-y) * 16px), 0)',
          }}
          decoding="async"
          loading="lazy"
        />
        <div className="film-grain" />
      </div>

      {showCanvas ? (
        <div className="absolute inset-0 z-[1] opacity-90">
          <PetCanvas hero overlay pointerEnabled textureSrc="/images/dog-01.webp" />
        </div>
      ) : null}

      <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-r from-stage via-stage/55 to-transparent sm:via-stage/55 lg:via-stage/40" />
      <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-t from-stage via-stage/10 to-stage/40 sm:via-transparent sm:to-stage/50" />

      <div className="relative z-10 mx-auto flex min-h-[100dvh] max-w-[var(--container-max)] flex-col justify-end px-[var(--section-padding-x)] pb-36 pt-36 sm:pb-28 lg:justify-center lg:pb-24">
        <div ref={brandRef} className="hero-enter relative max-w-xl">
          <p className="font-body text-xs uppercase tracking-[0.34em] text-dawn">
            Glen Austin · Midrand
          </p>
          <h1
            id="hero-heading"
            className="mt-4 font-display text-[clamp(2.5rem,9vw,4.25rem)] font-semibold leading-[1.04] tracking-[-0.022em] text-mist"
          >
            Halfway
            <br />
            House
          </h1>
          <p className="mt-3 font-display text-[clamp(1.35rem,3.4vw,2rem)] font-medium text-dawn">
            Veterinary Clinic
          </p>
          <p className="mt-6 max-w-md font-body text-base leading-relaxed text-white/70 sm:text-lg">
            SAVC-registered small-animal care on Pitzer Road, from first vaccines to surgery and X-rays.
          </p>
          <div className="pointer-events-auto mt-9 flex flex-wrap items-center gap-3">
            <MagneticCta href={siteConfig.phoneHref} variant="secondary">
              Call the clinic
            </MagneticCta>
            <MagneticCta href="#visit" onClick={handleVisitClick} variant="light">
              Get directions
            </MagneticCta>
          </div>
        </div>

        <button
          type="button"
          onClick={handleCareClick}
          className="pointer-events-auto mt-16 flex min-h-11 items-center self-start font-body text-xs uppercase tracking-[0.32em] text-white/70 transition-colors duration-200 ease-in-out hover:text-white"
        >
          Scroll into the consult
        </button>
      </div>
    </section>
  );
}
